import React, { useState } from "react";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { contact, business, serviceTypes } from "../mock/mock";
import Seo from "../seo/Seo";
import { PAGE_SEO } from "../seo/siteConfig";
import { breadcrumbSchema, webPageSchema } from "../seo/schema";
import Reveal from "../components/common/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const initial = { name: "", email: "", phone: "", service: "", message: "", website: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      toast.error("Please fill in your name, email, and service type.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(result.error || "We could not send your message. Please try again.");
      }

      setSubmitting(false);
      setForm(initial);
      toast.success("Message sent! We'll get back to you shortly.");
    } catch (error) {
      setSubmitting(false);
      toast.error(
        error instanceof Error ? error.message : "We could not send your message. Please try again."
      );
    }
  };

  return (
    <>
      <Seo
        title={PAGE_SEO["/contact"].title}
        description={PAGE_SEO["/contact"].description}
        path="/contact"
        schema={[
          webPageSchema({ ...PAGE_SEO["/contact"], path: "/contact" }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative min-h-[52vh] flex items-center overflow-hidden">
        <img
          src={contact.heroBg}
          alt="Contact Talk N' Fix"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        <div className="tnf-container relative pt-32 pb-16">
          <Reveal className="max-w-2xl">
            <span className="tnf-eyebrow text-[var(--tnf-orange)]">Get in touch</span>
            <h1 className="font-display text-white font-extrabold leading-[1.05] text-[40px] sm:text-[54px] mt-3">
              {contact.heroTitle}
            </h1>
            <p className="text-white/80 text-[17px] mt-5 max-w-xl leading-relaxed">
              {contact.heroText}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Info + Form */}
      <section className="py-20 lg:py-24">
        <div className="tnf-container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Info */}
            <Reveal className="lg:col-span-2">
              <span className="tnf-eyebrow">Contact Information</span>
              <h2 className="font-display text-[28px] sm:text-[34px] font-extrabold text-[#0e0e10] mt-3 leading-[1.12]">
                {contact.infoTitle}
              </h2>
              <p className="text-[#3b3b3b]/80 text-[16px] mt-4 leading-relaxed">
                {contact.infoText}
              </p>

              <div className="mt-8 space-y-4">
                <a href={business.phoneRaw} className="flex items-center gap-4 group">
                  <span
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(5,165,213,0.12)" }}
                  >
                    <Phone size={20} color="var(--tnf-orange)" />
                  </span>
                  <span>
                    <span className="block text-[12px] text-[#9a9aa2]">Call us</span>
                    <span className="text-[16px] font-semibold text-[#0e0e10] group-hover:text-[var(--tnf-orange)] transition-colors">
                      {business.phone}
                    </span>
                  </span>
                </a>
                <a href={`mailto:${business.email}`} className="flex items-center gap-4 group">
                  <span
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(37,158,205,0.1)" }}
                  >
                    <Mail size={20} color="var(--tnf-blue)" />
                  </span>
                  <span>
                    <span className="block text-[12px] text-[#9a9aa2]">Email us</span>
                    <span className="text-[16px] font-semibold text-[#0e0e10] group-hover:text-[var(--tnf-blue)] transition-colors">
                      {business.email}
                    </span>
                  </span>
                </a>
                <a
                  href={business.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <span className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#0e0e10]/[0.06]">
                    <MapPin size={20} color="#0e0e10" />
                  </span>
                  <span>
                    <span className="block text-[12px] text-[#9a9aa2]">Visit us</span>
                    <span className="text-[15px] font-semibold text-[#0e0e10] group-hover:underline">
                      {business.addressLine1}, {business.addressLine2}
                    </span>
                    <span className="block text-[13px] text-[var(--tnf-blue)] font-medium mt-0.5">
                      {business.floor}
                    </span>
                  </span>
                </a>
              </div>
            </Reveal>

            {/* Form */}
            <Reveal delay={120} className="lg:col-span-3">
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-[24px] border border-black/5 shadow-[0_18px_50px_rgba(0,0,0,0.06)] p-7 sm:p-9"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-1">
                    <Label className="text-[13px] font-medium text-[#0e0e10]">Full Name</Label>
                    <Input
                      name="name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="John Smith"
                      className="mt-2 h-12 rounded-xl border-black/10"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-[13px] font-medium text-[#0e0e10]">Email Address</Label>
                    <Input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@email.com"
                      className="mt-2 h-12 rounded-xl border-black/10"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-[13px] font-medium text-[#0e0e10]">Phone Number</Label>
                    <Input
                      name="phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="(732) 000-0000"
                      className="mt-2 h-12 rounded-xl border-black/10"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-[13px] font-medium text-[#0e0e10]">Service Type</Label>
                    <Select
                      name="service"
                      value={form.service}
                      onValueChange={(v) => update("service", v)}
                    >
                      <SelectTrigger className="mt-2 h-12 rounded-xl border-black/10">
                        <SelectValue placeholder="Choose your service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[13px] font-medium text-[#0e0e10]">Message</Label>
                    <Textarea
                      name="message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Tell us about your device and the issue..."
                      className="mt-2 min-h-[130px] rounded-xl border-black/10 resize-none"
                    />
                  </div>
                  <div
                    className="absolute opacity-0 pointer-events-none -z-10"
                    aria-hidden="true"
                  >
                    <Label htmlFor="contact-website">Website</Label>
                    <Input
                      id="contact-website"
                      name="website"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="tnf-pill mt-6 w-full sm:w-auto justify-center text-white pl-7 pr-2 py-2.5 disabled:opacity-70"
                  style={{ background: "var(--tnf-orange)" }}
                >
                  {submitting ? (
                    <>
                      Sending <Loader2 size={18} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Send Message
                      <span className="tnf-arrow w-8 h-8 bg-white">
                        <ArrowUpRight size={16} color="var(--tnf-orange)" />
                      </span>
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20 lg:pb-28">
        <div className="tnf-container">
          <Reveal className="mb-8">
            <span className="tnf-eyebrow">Find Us</span>
            <h2 className="font-display text-[28px] sm:text-[38px] font-extrabold text-[#0e0e10] mt-3 leading-[1.1]">
              Find Us With Ease at Freehold Raceway Mall
            </h2>
          </Reveal>
          <Reveal
            delay={100}
            className="rounded-[24px] overflow-hidden border border-black/5 shadow-[0_18px_50px_rgba(0,0,0,0.08)] min-h-[420px]"
          >
            <iframe
              title="Talk N' Fix map"
              src={business.mapEmbed}
              className="w-full h-full min-h-[420px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
